import type {
  BillingInterval,
  PaidPlanTier,
  PlanSet,
  RevenueCatEntitlementKey,
  RevenueCatOfferingKey,
  RevenueCatPackageId,
  RevenueCatProductKey,
} from '../../../types/src/subscription';

export interface RevenueCatPlanMapping {
  planSet: PlanSet;
  tier: PaidPlanTier;
  entitlement: RevenueCatEntitlementKey;
  offering: RevenueCatOfferingKey;
  products: Record<BillingInterval, RevenueCatProductKey>;
  nativePurchaseEnabled: boolean;
  webCheckoutEnabled: boolean;
}

type RevenueCatLookupKey = `${PlanSet}:${PaidPlanTier}`;

export const revenueCatPackageIds: Record<BillingInterval, RevenueCatPackageId> = {
  monthly: '$rc_monthly',
  yearly: '$rc_annual',
};

const revenueCatPlanMappings: Record<RevenueCatLookupKey, RevenueCatPlanMapping> = {
  'individual:plus': {
    planSet: 'individual',
    tier: 'plus',
    entitlement: 'individual_plus',
    offering: 'individual_plus',
    products: {
      monthly: 'tabbio_individual_plus_monthly',
      yearly: 'tabbio_individual_plus_yearly',
    },
    nativePurchaseEnabled: true,
    webCheckoutEnabled: true,
  },
  'individual:pro': {
    planSet: 'individual',
    tier: 'pro',
    entitlement: 'individual_pro',
    offering: 'individual_pro',
    products: {
      monthly: 'tabbio_individual_pro_monthly',
      yearly: 'tabbio_individual_pro_yearly',
    },
    nativePurchaseEnabled: true,
    webCheckoutEnabled: true,
  },
  'employer:plus': {
    planSet: 'employer',
    tier: 'plus',
    entitlement: 'employer_plus',
    offering: 'employer_plus',
    products: {
      monthly: 'tabbio_employer_plus_monthly',
      yearly: 'tabbio_employer_plus_yearly',
    },
    nativePurchaseEnabled: true,
    webCheckoutEnabled: true,
  },
  'employer:pro': {
    planSet: 'employer',
    tier: 'pro',
    entitlement: 'employer_pro',
    offering: 'employer_pro',
    products: {
      monthly: 'tabbio_employer_pro_monthly',
      yearly: 'tabbio_employer_pro_yearly',
    },
    nativePurchaseEnabled: true,
    webCheckoutEnabled: true,
  },
};

export const legacyRevenueCatEntitlements = [
  'premium_profile',
  'premium_creator',
  'company_hiring',
] as const;

export const legacyRevenueCatProductIds = [
  'tabbio_profile_monthly',
  'tabbio_profile_yearly',
  'tabbio_creator_monthly',
  'tabbio_creator_yearly',
  'tabbio_hiring_monthly',
  'tabbio_hiring_yearly',
  'tabbio_premium_profile_monthly',
  'tabbio_premium_profile_yearly',
  'tabbio_premium_creator_monthly',
  'tabbio_premium_creator_yearly',
] as const;

export function getRevenueCatPlanMapping(
  planSet: PlanSet,
  tier: PaidPlanTier,
): RevenueCatPlanMapping {
  return revenueCatPlanMappings[`${planSet}:${tier}`];
}

export function listRevenueCatPlanMappings() {
  return Object.values(revenueCatPlanMappings);
}

export function listRevenueCatV2ProductIds() {
  return listRevenueCatPlanMappings().flatMap((mapping) => [
    mapping.products.monthly,
    mapping.products.yearly,
  ]);
}

export function normalizeRevenueCatProductId(productId?: string | null) {
  if (!productId) {
    return null;
  }

  const normalized = productId.trim();
  if (!normalized) {
    return null;
  }

  const separatorIndex = normalized.indexOf(':');
  if (separatorIndex <= 0) {
    return normalized;
  }

  return normalized.slice(0, separatorIndex);
}

export function isRevenueCatV2ProductId(productId?: string | null) {
  const normalizedProductId = normalizeRevenueCatProductId(productId);
  if (!normalizedProductId) {
    return false;
  }

  return listRevenueCatV2ProductIds().includes(normalizedProductId as RevenueCatProductKey);
}

export function findRevenueCatPlanMappingByProductId(productId?: string | null) {
  const normalizedProductId = normalizeRevenueCatProductId(productId);
  if (!normalizedProductId) {
    return null;
  }

  return (
    listRevenueCatPlanMappings().find(
      (mapping) =>
        mapping.products.monthly === normalizedProductId ||
        mapping.products.yearly === normalizedProductId,
    ) ?? null
  );
}

export function getRevenueCatAppUserId(input: {
  userId: string;
  planSet: PlanSet;
  companyId?: string | null;
}) {
  if (input.planSet === 'employer') {
    const companyId = input.companyId?.trim();
    if (!companyId) {
      throw new Error('companyId is required for employer RevenueCat app user IDs');
    }

    return `company:${companyId}`;
  }

  return input.userId;
}

export function supportsNativeRevenueCatPurchase(planSet: PlanSet, tier: PaidPlanTier) {
  return getRevenueCatPlanMapping(planSet, tier).nativePurchaseEnabled;
}

export function supportsRevenueCatWebCheckout(planSet: PlanSet, tier: PaidPlanTier) {
  return getRevenueCatPlanMapping(planSet, tier).webCheckoutEnabled;
}
